import React, { useState, useEffect, useRef } from "react";
import { socket } from "../socket";
import { useGameTimer } from "../gameTimer";
import { t, tx } from "../i18n";
import { useCountdownTick } from "../sound";
import { PlayerAvatar } from "./PlayerIdentity";

// Tie-break duel: the players tied at the top answer a hard question. The
// fastest correct answer wins.
function TieBreak({ gameId, role, data }) {
  const [answer, setAnswer]   = useState("");
  const [sent, setSent]       = useState(null);   // answer time in ms
  const [review, setReview]   = useState(null);   // host validation phase
  const [valid, setValid]     = useState({});
  const [now, setNow]         = useState(Date.now());
  const inputRef = useRef(null);

  const { round, question, duration, startedAt, duelists, amIDueling } = data;

  useEffect(() => {
    socket.on("tieBreakAnswerAck", ({ ms }) => setSent(ms));
    socket.on("tieBreakReview", (r) => setReview(r));
    socket.on("tieBreakValidated", ({ playerId, valid: v }) =>
      setValid(prev => ({ ...prev, [playerId]: v })));
    return () => {
      ["tieBreakAnswerAck", "tieBreakReview", "tieBreakValidated"].forEach(e => socket.off(e));
    };
  }, []);

  // New tie-break round: start again from a blank screen
  useEffect(() => {
    setAnswer(""); setSent(null); setReview(null); setValid({});
    inputRef.current?.focus();
  }, [round]);

  useEffect(() => {
    if (review) return;
    const iv = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(iv);
  }, [review]);

  const remaining = Math.max(0, Math.ceil((duration - (now - startedAt)) / 1000));

  useCountdownTick(remaining, { active: !review });
  useGameTimer(review ? { remaining: 0, duration, idle: true } : { remaining, duration });
  const send = () => {
    if (sent !== null || !answer.trim()) return;
    socket.emit("tieBreakAnswer", { gameId, text: answer.trim() });
  };

  // ── Host validation phase ──────────────────────────────────────────────────
  if (review) {
    const anyValid = review.rows.some(r => valid[r.playerId]);
    return (
      <div className="TieBreak">
        <span className="tb-badge">{t("tieBreak.badge", { round: review.round })}</span>
        <h2 className="tb-question">{review.question}</h2>
        {review.expected && <p className="tb-expected">{tx("tieBreak.expected", { answer: <b>{review.expected}</b> })}</p>}
        <div className="tb-rows">
          {review.rows.map(r => (
            <div key={r.playerId} className={`tb-row${valid[r.playerId] ? " valid" : ""}`}>
              <PlayerAvatar player={r} size={34} />
              <span className="tb-row-name">{r.pseudo}</span>
              <span className="tb-row-answer">{r.answer || <em>{t("tieBreak.noAnswer")}</em>}</span>
              <span className="tb-row-ms">{r.ms !== null ? `${(r.ms / 1000).toFixed(1)}s` : "—"}</span>
              <button
                className={`pb-toggle${valid[r.playerId] ? " on" : ""}`}
                disabled={role !== "host"}
                onClick={() => socket.emit("tieBreakValidate", { gameId, playerId: r.playerId, valid: !valid[r.playerId] })}>
                {valid[r.playerId] ? t("tieBreak.markRight") : t("tieBreak.markWrong")}
              </button>
            </div>
          ))}
        </div>
        {role === "host" ? (
          <>
            <p className="tb-hint">
              {anyValid ? t("tieBreak.hintFastest") : t("tieBreak.hintNobody")}
            </p>
            <button className="btn btn-primary" onClick={() => socket.emit("tieBreakFinish", { gameId })}>
              {t("tieBreak.finish")}
            </button>
          </>
        ) : (
          <p className="tb-hint">{t("common.waitingForHost")}</p>
        )}
      </div>
    );
  }

  // ── Answering phase ────────────────────────────────────────────────────────
  return (
    <div className="TieBreak">
      <span className="tb-badge">{t("tieBreak.badge", { round })}</span>
      <div className="tb-duelists">
        {duelists.map((d, i) => (
          <React.Fragment key={d.playerId}>
            {i > 0 && <span className="tb-vs">{t("tieBreak.vs")}</span>}
            <div className="tb-duelist">
              <PlayerAvatar player={d} size={56} className="tb-duelist-avatar" />
              <span>{d.pseudo}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      {question.media_url && (
        /\.(jpe?g|png|gif|webp)$/i.test(question.media_url)
          ? <img src={question.media_url} alt="" className="quiz-media" />
          : <audio controls src={question.media_url} />
      )}
      <h2 className="tb-question">{question.question}</h2>
      <span className="tb-diff">{t("tieBreak.hardQuestion")}</span>

      {amIDueling ? (
        sent !== null ? (
          <p className="tb-sent">{tx("tieBreak.sent", { time: <b>{(sent / 1000).toFixed(1)}s</b> })}</p>
        ) : (
          <div className="tb-answer">
            <input ref={inputRef} value={answer} placeholder={t("quiz.yourAnswer")}
              onChange={e => setAnswer(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()} autoFocus />
            <button className="btn btn-primary" onClick={send} disabled={!answer.trim()}>{t("common.send")}</button>
          </div>
        )
      ) : (
        <p className="tb-hint">{t("tieBreak.spectator")}</p>
      )}
    </div>
  );
}

export default TieBreak;
